import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useConsultation } from "@/components/ConsultationModal";

export default function ServicePageLayout({ eyebrow, title, intro, image, imageAlt, offerings, process, stack, children }) {
  const { open } = useConsultation();
  return (
    <div data-testid="service-detail-page">
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,102,255,0.15),transparent_60%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400" data-testid="service-page-eyebrow">{eyebrow}</p>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl" data-testid="service-page-title">
              {title}
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">{intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/quote"
                data-testid="service-page-quote-button"
                className="group flex items-center gap-2 rounded-full bg-electric px-6 py-3 text-sm font-semibold text-white shadow-[0_0_25px_rgba(0,102,255,0.35)] transition-all hover:bg-cyan-400 hover:text-ink"
              >
                Get a Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <button
                type="button"
                onClick={open}
                data-testid="service-page-consultation-button"
                className="rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
              >
                Free Consultation
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="spotlight relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(3,7,18,0.7)]">
              <img src={image} alt={imageAlt} className="aspect-[16/10] w-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <Reveal>
          <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="service-offerings-title">What's included</h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {offerings.map((o, i) => (
            <Reveal key={o.title} delay={(i % 3) * 0.08}>
              <div className="card-glow h-full rounded-2xl border border-white/10 bg-navy-900/60 p-6" data-testid={`offering-${o.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/5">
                  <o.icon className="h-5 w-5 text-cyan-300" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">{o.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/5 bg-navy-900/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <Reveal>
                <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="service-process-title">How we deliver</h2>
              </Reveal>
              <div className="mt-8 space-y-6">
                {process.map((p, i) => (
                  <Reveal key={p.title} delay={i * 0.08}>
                    <div className="flex gap-5" data-testid={`process-step-${i + 1}`}>
                      <span className="font-mono text-sm text-cyan-400/80">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <h3 className="font-display text-base font-semibold text-white">{p.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div>
              <Reveal>
                <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="service-stack-title">Technology we trust</h2>
              </Reveal>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {stack.map((t) => (
                  <span key={t} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 font-mono text-xs uppercase tracking-wider text-slate-300" data-testid={`stack-chip-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {t}
                  </span>
                ))}
              </div>
              <Reveal delay={0.1}>
                <ul className="mt-10 space-y-3">
                  {["Fixed-scope proposals with transparent pricing", "Weekly demos and a dedicated project lead", "Security review and documentation included"].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      {children}
    </div>
  );
}
